// select — default skin module
// Token disposition: pass-through (no overrides)
// NO external imports — plain JavaScript only

export const styles = `
  :host {
    display: inline-flex;
    flex-direction: column;
    min-inline-size: var(--run-select-min-width, 10rem);
    position: relative;
  }

  [part="trigger"] {
    display: inline-flex;
    align-items: center;
    gap: var(--run-select-gap, var(--run-space-semantic-xs));
    inline-size: 100%;
    block-size: var(--run-select-height, 40px);
    padding-block: var(--run-select-padding-block, var(--run-space-semantic-xs));
    padding-inline: var(--run-select-padding-inline, var(--run-space-semantic-md));
    background: var(--run-select-trigger-bg, var(--run-color-surface-default));
    color: var(--run-select-color, var(--run-color-text-primary));
    border: var(--run-select-border-width, 1px) solid var(--run-select-trigger-border, var(--run-color-border-input-default));
    border-radius: var(--run-select-radius, var(--run-radius-semantic-control));
    font-size: var(--run-select-font-size, var(--run-font-size-base));
    font-weight: var(--run-select-font-weight, var(--run-font-semantic-weight-body));
    line-height: var(--run-select-line-height, var(--run-font-semantic-line-height-body));
    cursor: pointer;
    text-align: start;
    appearance: none;
    box-shadow: var(--run-select-shadow, var(--run-elevation-semantic-raised));
    transition:
      border-color var(--run-select-transition-duration, var(--run-motion-semantic-micro-duration)) var(--run-select-transition-easing, var(--run-motion-semantic-micro-easing)),
      box-shadow var(--run-select-transition-duration, var(--run-motion-semantic-micro-duration)) var(--run-select-transition-easing, var(--run-motion-semantic-micro-easing));
    anchor-name: --run-select-trigger;
  }

  [part="trigger"]:hover:not(:disabled) {
    border-color: var(--run-select-trigger-border-hover, var(--run-color-border-input-hover));
  }

  [part="trigger"]:focus-visible {
    outline: var(--run-select-focus-ring-width, 2px) solid var(--run-select-focus-ring-color, var(--run-color-focus-ring));
    outline-offset: var(--run-select-focus-ring-offset, 2px);
    border-color: var(--run-color-intent-primary-base);
    box-shadow: var(--run-select-shadow-focus, none);
  }

  :host([open]) [part="trigger"] {
    border-color: var(--run-color-intent-primary-base);
    box-shadow: var(--run-select-shadow-focus, none);
  }

  :host([invalid]) [part="trigger"] {
    border-color: var(--run-select-border-color-invalid, var(--run-color-feedback-danger-border));
  }

  :host([disabled]) [part="trigger"] {
    opacity: var(--run-opacity-state-disabled);
    cursor: not-allowed;
    border-color: var(--run-select-border-color-disabled, var(--run-color-border-subtle));
    background: var(--run-select-background-disabled, var(--run-color-surface-subtle));
    color: var(--run-select-color-disabled, var(--run-color-text-disabled));
  }

  :host([readonly]) [part="trigger"] {
    cursor: default;
  }

  .trigger__label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .trigger__label--placeholder {
    color: var(--run-select-color-placeholder, var(--run-color-text-tertiary));
  }

  .trigger__chevron {
    flex-shrink: 0;
    inline-size: var(--run-select-icon-size, var(--run-size-icon-sm));
    block-size: var(--run-select-icon-size, var(--run-size-icon-sm));
    color: var(--run-select-icon-color, var(--run-color-text-secondary));
    transition: transform var(--run-select-transition-duration, var(--run-motion-semantic-micro-duration)) var(--run-select-transition-easing, var(--run-motion-semantic-micro-easing));
  }

  :host([disabled]) .trigger__chevron {
    color: var(--run-select-icon-color-disabled, var(--run-color-text-disabled));
  }

  :host([open]) .trigger__chevron {
    transform: rotate(180deg);
  }

  [part="listbox"] {
    display: none;
  }

  [part="listbox"]:popover-open {
    display: flex;
    flex-direction: column;
    position: fixed;
    position-anchor: --run-select-trigger;
    inset-block-start: anchor(bottom);
    inset-inline-start: anchor(left);
    inline-size: anchor-size(width);
    min-inline-size: var(--run-select-listbox-min-width, anchor-size(width));
    max-block-size: var(--run-select-listbox-max-height, 18rem);
    overflow-y: auto;
    background: var(--run-select-listbox-background, var(--run-color-surface-overlay));
    border: var(--run-select-border-width, 1px) solid var(--run-select-listbox-border-color, var(--run-color-border-default));
    border-radius: var(--run-select-listbox-border-radius, var(--run-radius-semantic-control));
    box-shadow: var(--run-select-listbox-shadow, var(--run-elevation-semantic-overlay));
    padding-block: var(--run-select-listbox-padding-block, var(--run-space-semantic-2xs));
    margin: 0;
    padding-inline: 0;
    list-style: none;
    transition:
      opacity var(--run-select-listbox-transition-duration, var(--run-motion-semantic-entrance-duration)) var(--run-select-listbox-transition-easing, var(--run-motion-semantic-entrance-easing));
  }

  /* Robust open-state visibility. The :popover-open rule above depends on the
     Popover API + CSS Anchor Positioning (anchor()/anchor-size()), which is
     not reliably available and left the listbox display:none even when open
     (RUN-376 R2). This attribute-driven rule positions the listbox relative
     to the position:relative host with no anchor/popover dependency. Higher
     specificity than :popover-open, so it is authoritative when open. */
  :host([open]) [part="listbox"] {
    display: flex;
    flex-direction: column;
    position: absolute;
    inset-block-start: calc(100% + var(--run-space-semantic-2xs));
    inset-inline-start: 0;
    inline-size: 100%;
    min-inline-size: var(--run-select-listbox-min-width, 100%);
    max-block-size: var(--run-select-listbox-max-height, 18rem);
    overflow-y: auto;
    z-index: 1000;
    background: var(--run-select-listbox-background, var(--run-color-surface-overlay));
    border: var(--run-select-border-width, 1px) solid var(--run-select-listbox-border-color, var(--run-color-border-default));
    border-radius: var(--run-select-listbox-border-radius, var(--run-radius-semantic-control));
    box-shadow: var(--run-select-listbox-shadow, var(--run-elevation-semantic-overlay));
    padding-block: var(--run-select-listbox-padding-block, var(--run-space-semantic-2xs));
    margin: 0;
    padding-inline: 0;
    list-style: none;
  }

  @media (prefers-reduced-motion: reduce) {
    [part="trigger"],
    .trigger__chevron,
    [part="listbox"]:popover-open {
      transition: none;
    }
  }

  ::slotted([role="option"]) {
    display: flex;
    align-items: center;
    gap: var(--run-select-gap, var(--run-space-semantic-xs));
    padding-block: var(--run-select-option-padding-block, var(--run-space-semantic-xs));
    padding-inline: var(--run-select-option-padding-inline, var(--run-space-semantic-md));
    font-size: var(--run-select-option-font-size, var(--run-font-size-base));
    color: var(--run-select-option-color, var(--run-color-text-primary));
    background: var(--run-select-option-background, var(--run-color-surface-default));
    cursor: pointer;
    user-select: none;
  }

  ::slotted([role="option"]:hover) {
    background: var(--run-select-option-background-hover, var(--run-color-state-hover-overlay));
  }

  ::slotted([role="option"][aria-selected="true"]) {
    background: var(--run-select-option-background-selected, var(--run-color-state-selected-overlay));
    color: var(--run-select-option-color-selected, var(--run-color-text-primary));
  }

  ::slotted([role="option"][aria-selected="true"]:hover) {
    background: var(--run-select-option-background-selected-hover, var(--run-color-state-selected-overlay));
  }

  ::slotted([role="option"][aria-disabled="true"]) {
    color: var(--run-select-option-color-disabled, var(--run-color-text-disabled));
    cursor: not-allowed;
    opacity: var(--run-opacity-state-disabled);
  }

  .option-check {
    inline-size: var(--run-select-option-check-size, var(--run-size-icon-sm));
    block-size: var(--run-select-option-check-size, var(--run-size-icon-sm));
    color: var(--run-select-option-check-color, var(--run-color-intent-primary-base));
    flex-shrink: 0;
  }

  .listbox__empty {
    padding-block: var(--run-select-option-padding-block, var(--run-space-semantic-xs));
    padding-inline: var(--run-select-option-padding-inline, var(--run-space-semantic-md));
    color: var(--run-color-text-tertiary);
    font-size: var(--run-select-option-font-size, var(--run-font-size-base));
    cursor: default;
  }
`;

export function render(host) {
  const {
    value,
    placeholder,
    size,
    open,
    disabled,
    readonly,
    invalid,
    _activeDescendantId,
    shadowRoot,
  } = host; // declared in MVA SkinModule.render contract

  const hasValue = value !== '' && value != null;
  const labelClass = hasValue ? 'trigger__label' : 'trigger__label trigger__label--placeholder';
  const labelText = hasValue ? value : (placeholder || '');

  const expandedAttr = open ? 'true' : 'false';
  const disabledAttr = disabled ? 'true' : 'false';
  const requiredAttr = host.required ? 'true' : 'false';
  const invalidAttr = invalid ? 'true' : 'false';
  const readonlyAttr = readonly ? 'true' : 'false';
  const tabindexAttr = disabled ? '-1' : '0';
  const activedescAttr = _activeDescendantId ? ` aria-activedescendant="${_activeDescendantId}"` : '';

  // Chevron SVG — pass-through color via currentColor, uses icon-size token via width/height attributes removed in favour of CSS
  const chevronSvg = `<svg class="trigger__chevron" aria-hidden="true" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

  shadowRoot.innerHTML = `
    <button
      class="trigger"
      part="trigger"
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded="${expandedAttr}"
      aria-controls="listbox"
      aria-disabled="${disabledAttr}"
      aria-required="${requiredAttr}"
      aria-invalid="${invalidAttr}"
      aria-readonly="${readonlyAttr}"
      ${activedescAttr}
      tabindex="${tabindexAttr}"
      ${disabled ? 'disabled' : ''}
    >
      <slot name="prefix"></slot>
      <span class="${labelClass}">
        <slot name="placeholder">${labelText}</slot>
      </span>
      ${chevronSvg}
    </button>
    <div
      id="listbox"
      class="listbox"
      part="listbox"
      role="listbox"
      popover="manual"
    >
      <slot></slot>
    </div>
  `;
}